import { z } from 'zod';
import type { ProcessTriggerType } from './enums.js';

export const XERO_EVENT_CATEGORIES = ['INVOICE', 'CONTACT'] as const;
export type XeroEventCategory = (typeof XERO_EVENT_CATEGORIES)[number];

export const XERO_EVENT_TYPES = ['CREATE', 'UPDATE'] as const;
export type XeroEventType = (typeof XERO_EVENT_TYPES)[number];

export const XERO_INVOICE_TYPES = ['ACCREC', 'ACCPAY'] as const;
export type XeroInvoiceType = (typeof XERO_INVOICE_TYPES)[number];

export const XERO_INVOICE_STATUSES = [
  'DRAFT',
  'SUBMITTED',
  'AUTHORISED',
  'PAID',
  'VOIDED',
  'DELETED',
] as const;
export type XeroInvoiceStatus = (typeof XERO_INVOICE_STATUSES)[number];

export const xeroEventTriggerDefinitionSchema = z
  .object({
    eventCategory: z.enum(XERO_EVENT_CATEGORIES),
    eventTypes: z.array(z.enum(XERO_EVENT_TYPES)).min(1),
    invoiceTypes: z.array(z.enum(XERO_INVOICE_TYPES)).min(1).optional(),
    invoiceStatuses: z.array(z.enum(XERO_INVOICE_STATUSES)).min(1).optional(),
    runAsMemberId: z.number().int().positive().optional(),
  })
  .strict()
  .superRefine((val, ctx) => {
    if (val.eventCategory === 'INVOICE') {
      return;
    }
    for (const key of ['invoiceTypes', 'invoiceStatuses'] as const) {
      if (val[key] !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [key],
          message: `${key} is only valid when eventCategory is INVOICE`,
        });
      }
    }
  });

export type XeroEventTriggerDefinition = z.infer<typeof xeroEventTriggerDefinitionSchema>;

export const XERO_AUTOMATION_SHAPES = [
  'BILL_FROM_EVIDENCE',
  'INVOICE_FROM_DOCUMENT',
  'ON_XERO_EVENT',
  'OVERDUE_CHASE',
] as const;
export type XeroAutomationShape = (typeof XERO_AUTOMATION_SHAPES)[number];

export const XERO_AUTOMATION_TRIGGER_TYPES: Record<XeroAutomationShape, readonly ProcessTriggerType[]> = {
  BILL_FROM_EVIDENCE: ['EMAIL_RECEIVED', 'FILE_UPLOADED'],
  INVOICE_FROM_DOCUMENT: ['TAG_APPLIED'],
  ON_XERO_EVENT: ['XERO_EVENT'],
  OVERDUE_CHASE: ['SCHEDULE'],
};
